import React, { useEffect, useMemo, useRef, useState } from "react";
import { AssignedTask } from "../utils/TaskMaster";
import ScrollableTextBox from "./ScrollText";
import TimerProgressCircle from "./TimerComponet";
import TaskNavigationBar from "./TaskNavigationBar";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import confetti from "canvas-confetti";
import { PointsEarnedPopup } from "./PointCounter";
import { basicPoints } from "../utils/Points";
import { processRewardJob, updateTaskPoints } from "../utils/api";
import { logPerf, measureAsync } from "../utils/performance";
import { CompleteTaskButton, LoadingIndicator } from "./assets";
import { useNotifications } from "../contexts/NotificationsContext";
import { useClientExtensions } from "../contexts/ClientExtensionContext";

const COMPLETION_SOUND_MUTED_STORAGE_KEY = "dopatize:completion-sound-muted";

const getInitialCompletionSoundMuted = () => {
  try {
    return window.localStorage.getItem(COMPLETION_SOUND_MUTED_STORAGE_KEY) === "true";
  } catch (_) {
    return false;
  }
};


/**
 * TaskRunner
 * - Adds a bottom timeline bar that shows Previous / Current / Next task names
 *   with bubbles that fill when the respective task is complete.
 * - Adds optional "time left" and "finish time" summary (showTimeSummary prop)
 *   computed by summing remaining times across the current task and all tasks
 *   after it:  SUM(max(0, duration - working_time - elapsed)).
 *
 * Props
 * - assignedTasks: array of AssignedTask-like objects
 * - onCompleteAll?: () => void
 * - onBackToHome?: () => void
 * - showTimeSummary?: boolean (default true)
 * - stopAtLevelBoundary?: boolean (default false)
 */


const TaskRunner = ({
  assignedTasks,
  handleUpdateTask,
  onBackToHome,
  showTimeSummary = true,
  backHomeButtonRef,
  updatePoints = basicPoints,
  stopAtLevelBoundary = false,
  suppressRewardNotifications = true,
}) => {
  const { setSuppressRewardNotifications } = useNotifications();
  const { emitTaskEvents } = useClientExtensions();
  const [tasks, setTasks] = useState([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [currentTask, setCurrentTask] = useState(null);
  const [routineLevel, setRoutineLevel] = useState(0);
  const [routineCompleteOpen, setRoutineCompleteOpen] = useState(false);
  const [pendingRoutineIndex, setPendingRoutineIndex] = useState(null);
  const [timeLeft, setTimeLeft] = useState("00:00:00");
  const [timeLeftPercent, setTimeLeftPercent] = useState(100);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  const [isCompletionSoundMuted, setIsCompletionSoundMuted] = useState(
    getInitialCompletionSoundMuted
  );
  const alarmAudioRef = useRef(new Audio("/alarm.mp3"));
  const completeAudioRef = useRef(null);
  const pendingOperationsRef = useRef(new Set());
  const [isFinishing, setIsFinishing] = useState(false);

  // Points popup state
  const [popupProps, setPopupProps] = useState({
    open: false,
    breakdown: [],
    streakMultiplier: 0,
    totalPoints: 0,
  });

  const [afterPopupClose, setAfterPopupClose] = useState(null);

  useEffect(() => {
    if (!suppressRewardNotifications) return undefined;
    setSuppressRewardNotifications(true);
    return () => {
      setSuppressRewardNotifications(false);
    };
  }, [setSuppressRewardNotifications, suppressRewardNotifications]);

  const trackPendingOperation = (operation) => {
    if (!operation || typeof operation.finally !== 'function') return operation;
    pendingOperationsRef.current.add(operation);
    operation.then(
      () => pendingOperationsRef.current.delete(operation),
      () => pendingOperationsRef.current.delete(operation),
    );
    return operation;
  };

  const waitForPendingOperations = async () => {
    while (pendingOperationsRef.current.size > 0) {
      await Promise.allSettled(Array.from(pendingOperationsRef.current));
    }
  };

  const finishAndGoHome = async () => {
    setIsFinishing(true);
    stopAlarm();
    await waitForPendingOperations();
    await onBackToHome?.();
  };

  const handlePopupClose = () => {
    const nextAction = afterPopupClose;
    setPopupProps(prev => ({ ...prev, open: false }));
    setAfterPopupClose(null);
    if (nextAction && typeof nextAction === 'function') {
      nextAction();
    }
  };

  // Preload complete sfx if present in /assets
  useEffect(() => {
    try {
      const sfx = require.context("../assets", false, /complete_sound.*\.mp3$/);
      const files = sfx.keys().map(sfx);
      if (files?.length) {
        const audioFiles = files.map(src => {
          const audio = new Audio(src);
          audio.preload = 'auto';
          return audio;
        });
        completeAudioRef.current = audioFiles;
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (assignedTasks && assignedTasks.length > 0) {
      const taskInstances = assignedTasks.map((t) =>
        t instanceof AssignedTask ? t : new AssignedTask(t)
      );
      setTasks(taskInstances);
      setCurrentTaskIndex(0);
      setCurrentTask(taskInstances[0]);
      setRoutineLevel(taskInstances[0].level || 0);
      setRoutineCompleteOpen(false);
      setPendingRoutineIndex(null);
      setTimeLeft(taskInstances[0].timer.getTimeLeft());
      setTimeLeftPercent(taskInstances[0].timer.getPercentLeft());
      setIsTimerRunning(Boolean(taskInstances[0].start));
    }
  }, [assignedTasks]);

  useEffect(() => {
    if (!currentTask) return;

    if (currentTask.start) {
      currentTask.timer.start(currentTask.start);
    }
    setIsTimerRunning(Boolean(currentTask.timer.intervalId));
    setTimeLeft(currentTask.timer.getTimeLeft());
    setTimeLeftPercent(currentTask.timer.getPercentLeft());

    currentTask.timer.onComplete = () => {
      if (currentTask.alarm && !isAlarmPlaying) {
        setIsAlarmPlaying(true);
        alarmAudioRef.current.play();
      }
    };
    currentTask.timer.onUpdate = (t, percent) => {
      setTimeLeft(t);
      setTimeLeftPercent(percent);
    };
  }, [currentTask, isAlarmPlaying]);

  const stopAlarm = () => {
    if (isAlarmPlaying) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
      setIsAlarmPlaying(false);
    }
  };

  const emitEventsForTask = (task, eventTypes) => {
    if (!task?.task_id || !eventTypes?.length) return;
    emitTaskEvents(task.task_id, eventTypes, { assignedTaskId: task.id });
  };

  const handleStopTimer = ({ emitEvent = true } = {}) => {
    stopAlarm();
    currentTask.timer.stop();
    currentTask.start = null;
    setIsTimerRunning(false);
    const operation = trackPendingOperation(Promise.resolve(handleUpdateTask?.(currentTask)));
    operation.catch((error) => {
      console.error('Failed to save stopped timer:', error);
    });
    if (emitEvent) emitEventsForTask(currentTask, ['timer_stopped']);
    return operation;
  };

  const handleStartTimer = () => {
    if (!currentTask.start) {
      const now = new Date();
      currentTask.timer.start(now);
      currentTask.start = now;
    } else {
      currentTask.timer.start(currentTask.start);
    }
    setIsTimerRunning(true);
    const operation = trackPendingOperation(Promise.resolve(handleUpdateTask?.(currentTask)));
    operation.catch((error) => {
      console.error('Failed to save started timer:', error);
    });
    emitEventsForTask(currentTask, ['timer_started']);
    return operation;
  };

  const handleToggleTimer = () => {
    if (isTimerRunning) {
      handleStopTimer();
    } else {
      handleStartTimer();
    }
  };

  const handleToggleCompletionSound = () => {
    setIsCompletionSoundMuted((currentlyMuted) => {
      const nextMuted = !currentlyMuted;
      try {
        window.localStorage.setItem(
          COMPLETION_SOUND_MUTED_STORAGE_KEY,
          String(nextMuted)
        );
      } catch (_) {}
      return nextMuted;
    });
  };

  const moveToTask = (newIndex, { checkRoutineBoundary = false } = {}) => {
    const nextTask = tasks[newIndex];
    if (!nextTask) {
      finishAndGoHome();
      return;
    }

    const nextLevel = nextTask.level || 0;
    if (stopAtLevelBoundary && checkRoutineBoundary && nextLevel > routineLevel) {
      setPendingRoutineIndex(newIndex);
      setRoutineCompleteOpen(true);
      return;
    }

    setCurrentTaskIndex(newIndex);
    setCurrentTask(nextTask);
    setRoutineLevel(nextLevel);
  };

  const handleStartNextRoutine = () => {
    if (pendingRoutineIndex === null) {
      setRoutineCompleteOpen(false);
      return;
    }
    const nextTask = tasks[pendingRoutineIndex];
    setCurrentTaskIndex(pendingRoutineIndex);
    setCurrentTask(nextTask);
    setRoutineLevel(nextTask?.level || 0);
    setPendingRoutineIndex(null);
    setRoutineCompleteOpen(false);
  };

  const handleSelectTask = (newIndex) => {
    if (newIndex === currentTaskIndex || !tasks[newIndex]) return;
    stopAlarm();
    if (currentTask.timer.intervalId || currentTask.start) {
      handleStopTimer();
    } else {
      setIsTimerRunning(false);
    }
    setCurrentTaskIndex(newIndex);
    setCurrentTask(tasks[newIndex]);
    setRoutineLevel(tasks[newIndex].level || 0);
  };

  const handleCompleteTask = () => {
    if (!currentTask) return;
    const completedTask = currentTask;
    const timerWasRunning = Boolean(
      isTimerRunning || completedTask.timer.intervalId || completedTask.start
    );
    const completionStartedAt = performance.now();
    completedTask.complete = new Date();
    setTasks((currentTasks) => [...currentTasks]);
    let spread = 70;
    let particleCount = 100;
    if (currentTaskIndex === tasks.length - 1) {
      spread = 100;
      particleCount = 500;
    }


    confetti({
      particleCount,
      spread,
      origin: { y: 0.6 },
    });
    const sounds = completeAudioRef.current;
    if (!isCompletionSoundMuted && sounds?.length) {
      const idx = Math.floor(Math.random() * sounds.length);
      const sfx = sounds[idx];
      sfx.currentTime = 0;
      sfx.play().catch(() => {});
    }

    stopAlarm();
    handleStopTimer({ emitEvent: false }).catch((error) => {
      console.error('Failed to save task completion:', error);
    });
    const nextIndex = currentTaskIndex + 1;
    const nextTask = tasks[nextIndex];
    const showRoutineComplete = Boolean(
      stopAtLevelBoundary &&
      nextTask &&
      (nextTask.level || 0) > routineLevel
    );
    const afterCompletionNavigation = () => {
      if (!nextTask) {
        finishAndGoHome();
      } else if (showRoutineComplete) {
        setPendingRoutineIndex(nextIndex);
        setRoutineCompleteOpen(true);
      } else {
        moveToTask(nextIndex);
      }
    };
    const emitCompletionEventsAndContinue = () => {
      emitEventsForTask(
        completedTask,
        timerWasRunning ? ['timer_stopped', 'completed'] : ['completed'],
      );
      afterCompletionNavigation();
    };

    logPerf("frontend.task_runner.complete_task_ui", performance.now() - completionStartedAt, { taskId: completedTask.id });
    const scoringOperation = measureAsync(
      "frontend.task_runner.update_points",
      () => updateTaskPoints(completedTask.id, completedTask, 'standard'),
      { taskId: completedTask.id },
    )
      .then((result) => {
        const scoring = result.scoring || updatePoints(completedTask);
        const scoreBreakdown = { ...scoring };
        const totalPoints = scoreBreakdown.points;
        const streak = scoreBreakdown.streak;
        delete scoreBreakdown.points;
        delete scoreBreakdown.streak;
        delete scoreBreakdown.type;
        const breakdown = Object.entries(scoreBreakdown).map(([label, points]) => ({
          label,
          points,
        }));

        window.dispatchEvent(new CustomEvent('profileUpdate'));
        window.dispatchEvent(new CustomEvent('rewardsUpdate', { detail: { quiet: true } }));
        setAfterPopupClose(() => () => {
          if (result?.reward_job_pending) {
            const rewardOperation = processRewardJob()
              .then(() => {
                window.dispatchEvent(new CustomEvent('rewardsUpdate', { detail: { quiet: true } }));
              })
              .catch((error) => {
                console.error('Failed to start reward generation:', error);
              });
            trackPendingOperation(rewardOperation);
          }
          emitCompletionEventsAndContinue();
        });
        setPopupProps({
          open: true,
          breakdown,
          streakMultiplier: streak,
          totalPoints,
        });
      })
      .catch((error) => {
        console.error('Failed to update points:', error);
        const scoring = updatePoints(completedTask);
        const scoreBreakdown = { ...scoring };
        const totalPoints = scoreBreakdown.points;
        const streak = scoreBreakdown.streak;
        delete scoreBreakdown.points;
        delete scoreBreakdown.streak;
        const breakdown = Object.entries(scoreBreakdown).map(([label, points]) => ({
          label,
          points,
        }));
        setAfterPopupClose(() => emitCompletionEventsAndContinue);
        setPopupProps({
          open: true,
          breakdown,
          streakMultiplier: streak,
          totalPoints,
        });
      });
    trackPendingOperation(scoringOperation);
  };

  // ----- Helpers -----
  const formatHMS = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((s % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const sec = Math.floor(s % 60)
      .toString()
      .padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  // seconds left for the *current* task via its timer; fall back to duration-working_time

  const remainingSecondsTotal = useMemo(() => {
    if (!tasks?.length) return 0;
    // timeLeft is the timer tick that refreshes this aggregate total.
    void timeLeft;
    let seconds = 0;
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i];
      if ((t.level || 0) !== routineLevel || t.complete) continue;
      const timerRemaining = t.timer?.remainingTime ?? 0;
      if (timerRemaining > 0) {
        seconds += timerRemaining / 1000;
      }
    }
    return seconds;
    
  }, [tasks, routineLevel, timeLeft]);

  const eta = useMemo(() => {
    const d = new Date(Date.now() + remainingSecondsTotal * 1000);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }, [remainingSecondsTotal]);

  const label = (t) =>
    t?.full_title
      ? String(t.full_title.slice(-1)).toUpperCase()
      : t?.title || "—";

  // ----- Render -----
  if (isFinishing) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <LoadingIndicator text="Updating progress" />
      </Box>
    );
  }

  return (
    <Box>
      {currentTask ? (
        <>
          <ScrollableTextBox
            id="task-title-list"
            lines={currentTask.full_title?.slice(0, -1) ?? []}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "44px minmax(0, 1fr) 44px",
              alignItems: "center",
              gap: 1,
              mt: 2,
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box aria-hidden="true" />
            <Typography id="task-current-title" variant="h5" color="text.pink" align="center">
              {label(currentTask)}
            </Typography>
            <IconButton
              onClick={handleToggleCompletionSound}
              aria-label={isCompletionSoundMuted
                ? "Turn on task completion sounds"
                : "Mute task completion sounds"}
              aria-pressed={isCompletionSoundMuted}
              title={isCompletionSoundMuted
                ? "Turn on task completion sounds"
                : "Mute task completion sounds"}
              sx={{
                width: 44,
                height: 40,
                color: isCompletionSoundMuted ? "secondary.main" : "primary.main",
                bgcolor: "background.paper",
                border: "2px solid",
                borderColor: isCompletionSoundMuted ? "secondary.main" : "primary.main",
                borderRadius: "50%",
                boxShadow: isCompletionSoundMuted
                  ? "0 5px 0 #8f1857, 0 0 14px rgba(255, 45, 149, 0.35)"
                  : "0 5px 0 #007a8a, 0 0 14px rgba(0, 229, 255, 0.35)",
                transition: "transform 140ms ease, box-shadow 140ms ease",
                "&:hover": {
                  bgcolor: "background.paper",
                  transform: "translate(-2px, -2px)",
                },
                "&:active": {
                  transform: "translateY(3px)",
                  boxShadow: isCompletionSoundMuted
                    ? "0 2px 0 #8f1857, 0 0 8px rgba(255, 45, 149, 0.3)"
                    : "0 2px 0 #007a8a, 0 0 8px rgba(0, 229, 255, 0.3)",
                },
              }}
            >
              {isCompletionSoundMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </Box>

          <Box id="task-timer-control" display="flex" justifyContent="center" mt={2}>
            <TimerProgressCircle
              value={timeLeftPercent}
              label={timeLeft}
              size={200}
              onClick={handleToggleTimer}
              actionIcon={isTimerRunning ? <PauseIcon /> : <PlayArrowIcon />}
              actionLabel={isTimerRunning ? "Pause timer" : "Start timer"}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2} gap={1}>
            <CompleteTaskButton
              id="task-complete-control"
              onClick={handleCompleteTask}
            />
            {backHomeButtonRef && (
              <Box
                component="button"
                ref={backHomeButtonRef}
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => {
                  stopAlarm();
                  handleStopTimer();
                  finishAndGoHome();
                }}
                sx={{ display: "none" }}
              />
            )}
          </Box>

          <TaskNavigationBar
            tasks={tasks}
            currentTaskIndex={currentTaskIndex}
            onSelectTask={handleSelectTask}
          />

          <Box px={2}>
            {showTimeSummary && (
              <Box mt={3} textAlign="center">
                <Typography variant="h6" sx={{ color: "secondary.main" }}>
                  time left: {formatHMS(remainingSecondsTotal)}
                </Typography>
                <Typography  variant="h6" sx={{ color: "secondary.main" }}>
                  Finish at: {eta}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Typography>No tasks available.</Typography>
      )}
      <Dialog open={routineCompleteOpen} onClose={handleStartNextRoutine} fullWidth maxWidth="xs">
        <DialogTitle>Routine Complete</DialogTitle>
        <DialogContent>
          <Typography>
            Nice work. You finished every task in this round.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleStartNextRoutine}>
            Start Over
          </Button>
        </DialogActions>
      </Dialog>
      <PointsEarnedPopup {...popupProps} onClose={handlePopupClose} />
    </Box>
  );
};

export default TaskRunner;
